import React, { useState } from 'react'
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from '~/lib/ui/table'
import Modal from '../_layouts/Modal'
import { useCandidate, queryClient } from './CandidateProvider'
import safeParse from '~/lib/safeParse'
import { useQuery, useMutation } from '@tanstack/react-query'
import { apiDelete, apiGet } from '~/util/api'
import Pagination from './Pagination'
import CloseReviewDialog from './ConfirmDeleteDialog'
import { Button } from '../_atoms/index'
import { useToast } from '~/lib/ui/toast'
import CreateCoachNote from './CreateCoachNote'
import { useFormatTime } from '~/hooks/useFormatTime'

const CoachNotesSection = () => {
  const [modalOpen, setModalOpen] = useState(false)
  const [selectedNote, setSelectedNote] = useState(null)
  const handleModalClose = () => {
    setModalOpen(false)
  }
  const handleNoteClick = (note) => {
    setSelectedNote(note)
    setModalOpen(true)
  }

  const groupedCells = ['Pre-Engagement', 'Engaged', 'Post-Engagement']

  const modalCells = [
    'Created',
    'Author',
    'Type',
    'Duration',
    'Topics',
    'Privacy Settings',
    'Detail',
    'Transcript',
    'Action',
  ]
  const [page, setPage] = React.useState(1)
  const { candidateCoachNotes } = useCandidateCoachNotes()
  const { all: allCoachNotes } = candidateCoachNotes || {}

  const PAGE_SIZE = 25

  const total = allCoachNotes?.length || 0

  // Paginate activities
  const paginated = React.useMemo(() => {
    if (!allCoachNotes) return []
    const start = (page - 1) * PAGE_SIZE
    return allCoachNotes.slice(start, start + PAGE_SIZE)
  }, [allCoachNotes, page])

  return (
    <>
      <CreateCoachNote>
        <Button className="mb-4">New Coach Note</Button>
      </CreateCoachNote>
      <Table
        className="w-full border-separate"
        tableClass="rounded-2xl border border-silver mb-8"
      >
        <CoachNotesHeading cells={groupedCells} />
        <TableBody>
          <GroupedCoachNotesRow
            setModalOpen={setModalOpen}
            handleNoteClick={handleNoteClick}
          />
        </TableBody>
      </Table>
      <CoachNotesModal
        modalOpen={modalOpen}
        handleModalClose={handleModalClose}
        modalCells={modalCells}
        selectedNote={selectedNote}
      />
      <Table
        className="w-full border-separate"
        tableClass="w-full rounded-2xl border border-silver"
      >
        <CoachNotesHeading cells={modalCells} />
        <TableBody>
          {paginated.map((coachNote) => (
            <CoachNotesRow
              key={`all-coach-notes-${coachNote.id}`}
              note={coachNote}
            />
          ))}
        </TableBody>
      </Table>
      {total > PAGE_SIZE && (
        <Pagination
          page={page}
          pageSize={PAGE_SIZE}
          total={total}
          onPageChange={setPage}
        />
      )}
    </>
  )
}

export default CoachNotesSection

export const useCandidateCoachNotes = () => {
  const { candidate: { id: candidateId } = {} } = useCandidate()

  const queryPath = `/coach/dashboard_candidates/${candidateId}/coach_notes.json`

  const queryFn = () => apiGet(queryPath).then((res) => res.data)
  const { data, error, isFetching } = useQuery({
    queryKey: ['candidateCoachNotes', candidateId],
    queryFn,
    enabled: !!candidateId,
  })

  if (error) {
    console.error('Error retrieving coach notes:', error)
    return null
  }

  const candidateCoachNotes = data?.candidateCoachNotes || []
  return { candidateCoachNotes, isFetching }
}

const CoachNotesModal = ({
  modalOpen,
  handleModalClose,
  modalCells,
  selectedNote,
}) => {
  return (
    <Modal
      open={modalOpen}
      onClose={handleModalClose}
      title="Coach Note Detail"
      size="xl"
    >
      <Table className="w-full">
        <TableHeader className="h-6 bg-intoo-blue-medium">
          <TableRow className="text-lg text-white hover:bg-intoo-blue-medium">
            {modalCells.map((cell, index) => (
              <TableHead key={index} className="font-medium text-white">
                {cell}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {selectedNote && <CoachNotesRow note={selectedNote} />}
        </TableBody>
      </Table>
    </Modal>
  )
}

const GroupedCoachNotesRow = ({ handleNoteClick }) => {
  const { candidateCoachNotes } = useCandidateCoachNotes()
  const { preEngagement, duringEngagement, postEngagement } =
    candidateCoachNotes || {}
  return (
    <TableRow className="odd:bg-white even:bg-[#e2e2e2] even:bg-opacity-[29%]">
      <TableCell>
        <CoachNotesCell
          notes={preEngagement}
          handleNoteClick={handleNoteClick}
        />
      </TableCell>
      <TableCell>
        <CoachNotesCell
          notes={duringEngagement}
          handleNoteClick={handleNoteClick}
        />
      </TableCell>
      <TableCell>
        <CoachNotesCell
          notes={postEngagement}
          handleNoteClick={handleNoteClick}
        />
      </TableCell>
    </TableRow>
  )
}

const CoachNotesRow = ({ note }) => {
  const {
    coachId,
    candidate: { id: candidateId },
  } = useCandidate()
  const showActions =
    note.user_id === coachId && note.topics !== 'Resume: Assigned to Coach'
  const [showConfirmDeleteDialog, setShowConfirmDeleteDialog] = useState(false)
  const toggleDialog = () => {
    setShowConfirmDeleteDialog(!showConfirmDeleteDialog)
  }
  const { toast } = useToast()

  const deleteNote = async () => {
    const deletePath = `/coach/candidates/${candidateId}/coach_notes/${note.id}.json`
    const response = await apiDelete(deletePath, note)
    return response
  }

  const deleteNoteMutation = useMutation({
    mutationFn: deleteNote,
    onSuccess: () => {
      toggleDialog()
      queryClient.invalidateQueries(['serviceRequests', candidateId])
      toast({
        title: 'Success',
        description: `Coach note deleted successfully.`,
      })
    },
    onError: (error) => {
      console.error(`Error deleting coach note:`, error)
      const description =
        error?.response?.data?.error ||
        `An error occurred while deleting the coach note.`
      console.log('Error description:', description)
      toast({
        description,
        title: `Could Not Delete Coach note`,
        variant: 'destructive',
        duration: 10_000,
      })
    },
  })

  const deleteCoachNote = () => deleteNoteMutation.mutate()
  const formatTime = useFormatTime()

  const authorOnly =
    note.privacy_settings === 'Self' ||
    note.privacy_settings === 'Selves' ||
    note.privacy_settings === null ||
    note.privacy_settings.length === 0

  return (
    <TableRow className="odd:bg-white even:bg-[#e2e2e2] even:bg-opacity-[29%]">
      <TableCell>{formatTime(note.created_at, 'short')}</TableCell>
      <TableCell>{note.author}</TableCell>
      <TableCell>{note.note_type}</TableCell>
      <TableCell>{note.duration}</TableCell>
      <TableCell>{note.topics && safeParse(note.topics)}</TableCell>
      <TableCell>
        {authorOnly
          ? 'Author'
          : note.privacy_settings.replace('Selves', 'Author')}
      </TableCell>
      <TableCell>
        {' '}
        <FormattedNoteCell note={note} />
      </TableCell>
      <TableCell>
        {' '}
        {note.transcript
          ? safeParse(note.transcript.replace(/\n/g, '<br />'))
          : 'N/A'}
      </TableCell>
      <TableCell>
        {showActions && (
          <>
            <CreateCoachNote coachNote={note}>
              <Button variant="link">Edit</Button>
            </CreateCoachNote>{' '}
            |{' '}
            <Button variant="link" onClick={toggleDialog}>
              Delete
            </Button>
            <CloseReviewDialog
              coachNote={note}
              toggleDialog={toggleDialog}
              showConfirmDeleteDialog={showConfirmDeleteDialog}
              deleteCoachNote={deleteCoachNote}
            />
          </>
        )}
      </TableCell>
    </TableRow>
  )
}

const FormattedNoteCell = ({ note }) => {
  const formatNote = (str) => {
    return str.split(/\n/g)
  }
  const hidden = note?.note?.includes('(hidden)')

  if (!note?.note) return 'N/A'
  if (hidden) return 'This note is hidden.'

  return formatNote(note.note).map((line, index) => (
    <span key={index}>
      {line}
      <br />
    </span>
  ))
}

const CoachNotesCell = ({ notes, handleNoteClick }) => {
  const formatTime = useFormatTime()
  if (!notes || notes.length < 1) return null
  return (
    <>
      {notes.map((note) => {
        const { id, note_type: noteType, created_at: createdAt } = note || {}
        return (
          <div key={id} className="mb-2">
            <p className="text-xs text-gray-500">
              {formatTime(createdAt, 'short')} -{' '}
              <span
                className="cursor-pointer text-intoo-blue-dark underline"
                onClick={() => handleNoteClick(note)}
              >
                {noteType}
              </span>
            </p>
          </div>
        )
      })}
    </>
  )
}

const CoachNotesHeading = ({ cells }) => {
  return (
    <TableHeader className="h-6 bg-intoo-blue-medium">
      <TableRow className="text-lg text-white hover:bg-intoo-blue-medium">
        {cells.map((cell, index) => (
          <TableHead key={index} className="font-medium text-white">
            {cell}
          </TableHead>
        ))}
      </TableRow>
    </TableHeader>
  )
}
